import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, KeyRound, Lock, Mail } from 'lucide-react';
import { useLanguage } from '../../assets/js/LanguageContext';
import SectionEyebrow from '../common/SectionEyebrow';
import IconTile from '../common/IconTile';
import { FormField, PasswordField } from '../common/FormField';
import { authErrorKey } from '../../utils/authErrors';
import Footer from '../Footer';
import '../../assets/styles/SectionBand.css';
import '../../assets/styles/FormCard.css';
import '../../assets/styles/PinPage.css';

const API = process.env.REACT_APP_API_URL;

const Restore = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState({ text: '', isError: false });
  const [sending, setSending] = useState(false);

  const showError = (text) => setMessage({ text, isError: true });

  const handleEmailSubmit = async (event) => {
    event.preventDefault();
    if (sending) return;
    setSending(true);
    setMessage({ text: '', isError: false });

    try {
      const res = await fetch(`${API}/restore`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, language })
      });
      if (res.ok) {
        setMessage({ text: t('reset_code_sent'), isError: false });
        setStep(2);
      } else {
        showError(t(res.status === 429 ? 'auth_error_too_many' : 'reset_error_send'));
      }
    } catch (error) {
      console.error('Error al pedir el código de recuperación:', error);
      showError(t('reset_error_send'));
    } finally {
      setSending(false);
    }
  };

  const handleResetSubmit = async (event) => {
    event.preventDefault();
    if (sending) return;
    if (newPassword !== confirmPassword) {
      showError(t('reset_error_mismatch'));
      return;
    }
    setSending(true);
    setMessage({ text: '', isError: false });

    try {
      const res = await fetch(`${API}/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code, newPassword })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        showError(t(authErrorKey(res.status === 429 ? 'too_many_attempts' : data.code, 'reset_error_reset')));
        return;
      }

      setMessage({ text: t('reset_success'), isError: false });

      // Entramos directamente con la nueva contraseña
      const loginRes = await fetch(`${API}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password: newPassword })
      });
      const loginData = await loginRes.json();
      if (loginRes.ok) {
        localStorage.setItem('token', loginData.token);
        if (loginData.refreshToken) {
          localStorage.setItem('refreshToken', loginData.refreshToken);
        }
        window.dispatchEvent(new Event('authChange'));
        navigate('/dashboard');
      } else {
        showError(t('reset_error_login'));
      }
    } catch (error) {
      console.error('Error al restablecer la contraseña:', error);
      showError(t('reset_error_login'));
    } finally {
      setSending(false);
    }
  };

  const feedback = message.text && (
    <p className={message.isError ? 'tp-form__alert' : 'tp-form__note'} role={message.isError ? 'alert' : 'status'}>
      {message.text}
    </p>
  );

  return (
    <div className="brand-page-stack pin-page">
      <section className="section-band">
        <div className="section-band__inner tp-pin">
          <div className="tp-pin__main">
            <h1 className="tp-sr-only">{t('reset_title')}</h1>
            <div aria-hidden="true">
              <SectionEyebrow>{t('reset_title')}</SectionEyebrow>
            </div>
            <p className="section-band__lead tp-pin__lead">
              {t(step === 1 ? 'reset_lead' : 'reset_code_lead')}
            </p>

            {step === 1 ? (
              <form className="tp-form tp-pin__form" onSubmit={handleEmailSubmit}>
                <FormField
                  id="reset-email"
                  type="email"
                  label={t('login_placeholder_email')}
                  icon={<Mail />}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('login_placeholder_email')}
                  autoComplete="email"
                  required
                />
                {feedback}
                <button type="submit" className="btn btn--primary tp-form__submit" disabled={sending}>
                  {t('reset_send_btn')}
                  <ArrowRight size={16} strokeWidth={2.25} aria-hidden="true" />
                </button>
                <p className="tp-form__footer">
                  {t('reset_no_account')}{' '}
                  <Link to="/sign-up" className="tp-form__link">{t('reset_signup')}</Link>
                </p>
              </form>
            ) : (
              <form className="tp-form tp-pin__form" onSubmit={handleResetSubmit}>
                <FormField
                  id="reset-code"
                  label={t('login_code_placeholder')}
                  icon={<KeyRound />}
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder={t('login_code_placeholder')}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  required
                />
                <PasswordField
                  id="reset-new-password"
                  label={t('reset_new_password')}
                  icon={<Lock />}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder={t('reset_new_password')}
                  autoComplete="new-password"
                  showLabel={t('form_show_password')}
                  hideLabelText={t('form_hide_password')}
                  required
                />
                <PasswordField
                  id="reset-confirm-password"
                  label={t('reset_confirm_password')}
                  icon={<Lock />}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder={t('reset_confirm_password')}
                  autoComplete="new-password"
                  showLabel={t('form_show_password')}
                  hideLabelText={t('form_hide_password')}
                  required
                />
                {feedback}
                <button type="submit" className="btn btn--primary tp-form__submit" disabled={sending}>
                  {t('reset_submit_btn')}
                  <ArrowRight size={16} strokeWidth={2.25} aria-hidden="true" />
                </button>
              </form>
            )}
          </div>

          <div className="tp-pin__media">
            <IconTile accent="#7C3AED" icon={<KeyRound size="1em" strokeWidth={1.25} />} />
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default Restore;
